import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { api } from '../../api';
import { ApiError, type Indicacao, type Indicado } from '../../api/types';
import { numero, t } from '../../i18n/idioma';
import { GumpWindow } from '../GumpWindow';
import { IconeHud } from '../hud/icones';

/**
 * Mais › Indicar amigos (techspec-indicacoes §04): o link da conta, os números, os indicados e as recompensas (em
 * breve, sem promessa). O indicado aparece só pelo nome do personagem.
 */
const CODIGO = /^[a-z0-9]{3,16}$/;
const NA_LISTA = 3;

export function linkDoCodigo(codigo: string): string {
  return `${window.location.origin}/c/${codigo}`;
}

async function copiar(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    // Sem permissão de área de transferência (http, iframe): o jeito antigo.
    const ta = document.createElement('textarea');
    ta.value = texto;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch { ok = false; }
    ta.remove();
    return ok;
  }
}

function dataCurta(iso: string): string {
  const d = new Date(iso);
  const hoje = new Date();
  const dia = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dias = Math.round((dia(hoje) - dia(d)) / 86_400_000);
  if (dias <= 0) return t('indicacoes.hoje');
  if (dias === 1) return t('indicacoes.ontem');
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function IndicarGump({ shown, onClose }: { shown: boolean; onClose(): void }) {
  const [dados, setDados] = useState<Indicacao | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState('');
  const [erroCodigo, setErroCodigo] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [todos, setTodos] = useState(false);
  const [recado, setRecado] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setDados(await api.indicacoes());
      setErro(null);
    } catch (e) {
      setErro(e instanceof Error ? e.message : t('indicacoes.erro'));
    }
  }, []);

  useEffect(() => { if (shown) void carregar(); }, [shown, carregar]);

  useEffect(() => {
    if (!recado) return;
    const id = window.setTimeout(() => setRecado(null), 3000);
    return () => window.clearTimeout(id);
  }, [recado]);

  const codigo = dados?.codigo ?? null;
  const link = codigo ? linkDoCodigo(codigo) : '';
  const hostDoLink = window.location.host;
  const travado = !!dados?.podeTrocarEm && new Date(dados.podeTrocarEm).getTime() > Date.now();

  const copiarLink = async () => {
    if (!codigo) return;
    setRecado(t(await copiar(link) ? 'indicacoes.copiado' : 'indicacoes.copiarFalhou'));
  };
  const copiarConvite = async () => {
    if (!codigo) return;
    setRecado(t(await copiar(t('indicacoes.textoConvite', { link })) ? 'indicacoes.conviteCopiado' : 'indicacoes.copiarFalhou'));
  };

  const validar = (v: string): string | null => {
    if (!CODIGO.test(v)) return t('indicacoes.codigoFormato');
    return null;
  };
  const salvar = async () => {
    const v = rascunho.trim().toLowerCase();
    if (v === codigo) { setEditando(false); return; }
    const e = validar(v);
    if (e) { setErroCodigo(e); return; }
    setSalvando(true);
    try {
      const novo = await api.indicacoesTrocarCodigo(v);
      setDados(novo);
      setEditando(false);
      setErroCodigo(null);
      setRecado(t('indicacoes.trocado', { link: `${hostDoLink}/c/${novo.codigo ?? v}` }));
    } catch (err) {
      setErroCodigo(err instanceof ApiError || err instanceof Error ? err.message : t('indicacoes.erro'));
    } finally {
      setSalvando(false);
    }
  };

  let corpo: ReactNode;
  if (erro && !dados) corpo = <p className="in-vazio" role="alert">{erro}</p>;
  else if (!dados) corpo = <p className="in-vazio">{t('comum.carregando')}</p>;
  else if (!dados.ativo) corpo = <p className="in-vazio">{t('indicacoes.desligadas')}</p>;
  else {
    const lista = dados.indicados;
    const vis = todos ? lista : lista.slice(0, NA_LISTA);
    const teto = dados.marcos[dados.marcos.length - 1] ?? 1;
    const v = rascunho.trim().toLowerCase();
    const erroAoVivo = editando && v && v !== codigo ? validar(v) : null;
    corpo = (
      <>
        <h3 className="in-sec">{t('indicacoes.seuLink')} <span>{t('indicacoes.seuLinkDica')}</span></h3>
        {!codigo ? (
          <p className="in-vazio">{t('indicacoes.semPersonagem')}</p>
        ) : editando ? (
          <div className="in-link">
            <div className="in-edit">
              <div className="in-edit__l">
                <span className="in-edit__pre">{hostDoLink}/c/</span>
                <input
                  value={rascunho}
                  maxLength={16}
                  spellCheck={false}
                  autoComplete="off"
                  autoFocus
                  aria-label={t('indicacoes.novoCodigo')}
                  onChange={e => { setRascunho(e.target.value.toLowerCase().replace(/\s/g, '')); setErroCodigo(null); }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); void salvar(); }
                    if (e.key === 'Escape') { e.stopPropagation(); setEditando(false); }
                  }}
                />
                <button type="button" className="btn btn--tiny btn--ghost" onClick={() => setEditando(false)}>{t('comum.cancelar')}</button>
                <button type="button" className="btn btn--tiny btn--primary" disabled={salvando || !!erroAoVivo || !v} onClick={() => void salvar()}>
                  {t('indicacoes.salvar')}
                </button>
              </div>
              <small className={erroCodigo || erroAoVivo ? 'is-erro' : undefined} role={erroCodigo ? 'alert' : undefined}>
                {erroCodigo ?? erroAoVivo ?? t('indicacoes.regraCodigo')} {t('indicacoes.regraTroca')}
              </small>
            </div>
          </div>
        ) : (
          <div className="in-link">
            <div className="in-link__l">
              <div className="in-url" title={link}>{hostDoLink}/c/<b>{codigo}</b></div>
              <button type="button" className="btn btn--primary" onClick={() => void copiarLink()}>{t('indicacoes.copiarLink')}</button>
            </div>
            <div className="in-link__a">
              <button type="button" className="btn btn--tiny btn--ghost" onClick={() => void copiarConvite()}>{t('indicacoes.copiarConvite')}</button>
              <button type="button" className="btn btn--tiny btn--ghost" disabled={travado}
                onClick={() => { setRascunho(codigo); setErroCodigo(null); setEditando(true); }}>{t('indicacoes.trocar')}</button>
              <small>{travado ? t('indicacoes.trocaTravada', { data: dataCurta(dados.podeTrocarEm!) }) : t('indicacoes.daConta')}</small>
            </div>
          </div>
        )}

        <div className="in-nums">
          <Numero valor={dados.visitas} rotulo={t('indicacoes.visitas')} sub={t('indicacoes.visitasDica')} />
          <Numero valor={dados.contas} rotulo={t('indicacoes.contas')} sub={t('indicacoes.contasDica')} />
          <Numero valor={dados.jogando} rotulo={t('indicacoes.jogando')} sub={t('indicacoes.jogandoDica')} ouro />
        </div>

        <h3 className="in-sec">{t('indicacoes.seusIndicados')} <span>{t('indicacoes.soNome')}</span></h3>
        {lista.length === 0 ? (
          <div className="in-vazio">{t('indicacoes.nenhum')}</div>
        ) : (
          <div className="in-lista">
            {vis.map((i, k) => <LinhaIndicado key={`${i.em}-${k}`} indicado={i} />)}
            {lista.length > NA_LISTA && (
              <button type="button" className="in-mais-l" onClick={() => setTodos(x => !x)}>
                {todos ? t('indicacoes.menos') : t('indicacoes.todos', { n: lista.length })}
              </button>
            )}
          </div>
        )}

        <div className="in-rec">
          <div className="in-rec__t">
            <IconeHud nome="indicar" className="in-rec__ico" />
            <b>{t('indicacoes.recompensas')}</b>
            <span>{t('indicacoes.emBreve')}</span>
          </div>
          <div className="in-marcos">
            {dados.marcos.map(m => (
              <div key={m} className={dados.jogando >= m ? 'in-m is-feito' : 'in-m'}>
                <span className="in-m__ico" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
                </span>
                <b>{m}</b>
                <small>{t(m > 1 ? 'indicacoes.amigosJogando' : 'indicacoes.amigoJogando')}</small>
              </div>
            ))}
          </div>
          <div className="in-barra" aria-hidden="true"><i style={{ width: `${Math.min(100, Math.round(100 * dados.jogando / teto))}%` }} /></div>
          <p>{t('indicacoes.recompensasTexto', { n: dados.jogando })}</p>
        </div>

        <ul className="in-regras">
          <li>{t('indicacoes.regra1', { dias: dados.validade })}</li>
          <li>{t('indicacoes.regra2', { dias: dados.diasJogo })}</li>
          <li>{t('indicacoes.regra3')}</li>
        </ul>
      </>
    );
  }

  return (
    <GumpWindow
      title={t('indicacoes.titulo')}
      anchor={{ left: 380, top: 86 }}
      className="win--indica"
      posicao="indicar"
      shown={shown}
      comeSlots
      headLead={<IconeHud nome="indicar" className="in__ico-titulo" />}
      onClose={() => { if (editando) setEditando(false); else onClose(); }}
    >
      <div className="in">
        {corpo}
        {recado && <div className="in-recado" role="status"><IconeHud nome="indicar" /><span>{recado}</span></div>}
      </div>
    </GumpWindow>
  );
}

function Numero({ valor, rotulo, sub, ouro }: { valor: number; rotulo: string; sub: string; ouro?: boolean }) {
  return (
    <div className={ouro ? 'in-n is-ouro' : 'in-n'}>
      <b>{numero(valor)}</b>
      <span>{rotulo}</span>
      <small>{sub}</small>
    </div>
  );
}

function LinhaIndicado({ indicado: i }: { indicado: Indicado }) {
  const estado = t(`indicacoes.estado${i.estado}`);
  return (
    <div className="in-l">
      <span className={i.nome ? 'in-av' : 'in-av is-vazio'} aria-hidden="true">{i.nome ? i.nome[0] : '?'}</span>
      <span className="in-l__txt">
        <span className={i.nome ? 'in-l__n' : 'in-l__n is-sem'}>{i.nome ?? t('indicacoes.semPersonagemDele')}</span>
        <span className="in-l__m">{t('indicacoes.entrou', { quando: dataCurta(i.em) })}</span>
      </span>
      <span className="in-est" title={estado}>
        {[1, 2, 3].map(k => <i key={k} className={i.estado >= k ? 'is-on' : undefined} />)}
        <span>{estado}</span>
      </span>
    </div>
  );
}
